import * as React from 'react';
import { ArrowBack, EmailOutlined, Link } from "@mui/icons-material";
import { Icon, IconButton, Snackbar } from "@mui/material";
import LabelledIconButton from "modules/common/components/buttons/labelledIconButton";
import { createBodyAndEncode, createBodyAndEncodeOnlyUrl, generateMailTo, getDefaultEmailBody, getDefaultEmailSubject, getFacebookLink, getLinkedInLink, getTwitterLink } from 'utils/shareHelpers';
import { ContentType, IShareLinkv1, LINK_COPIED_SNACKBAR_TEXT, LOADING_SNACKBAR_TEXT, ShareContentMapType } from './models';

import "./sharingPages.sass";
import { sharingApi } from 'api/instances';

interface ComponentProps {
    onCloseDialog: () => void;
    tenantName: string;
    tenantId: string;
    contentTitle: string;
    contentId: string;
    contentType: ContentType;
    customShareUrl: string; //Replaces share system if populated. Validation means that if a URL is here it should be good.
    isAuthenticated: boolean;
}

const PublicShareScreen: React.FunctionComponent<ComponentProps> = (props) => {
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarText, setSnackbarText] = React.useState("");
    const validShareUrl = !!props.customShareUrl && props.customShareUrl !== "";
    const headerLabel = props.isAuthenticated ? "Share publicly" : "Share this post";

    //In the future this may need to be more dynamic if we have more public share options.
    const shareType = validShareUrl ? ShareContentMapType.CustomShareLink : ShareContentMapType.PortalPostPublic;
    const linkDetails:IShareLinkv1 = {contentType: shareType, contentId: props.contentId};

    const setSnackbarAndText = (text: string, open: boolean) => {
        setSnackbarText(text);
        setSnackbarOpen(open);
    }

    const onLinkRequested = async (linkDetails: IShareLinkv1) => {
        setSnackbarAndText(LOADING_SNACKBAR_TEXT, true);

        return await sharingApi.getShareLinkAnonymous(props.tenantId, linkDetails);
    }

    const onXClicked = async() => {
        const url = await onLinkRequested(linkDetails);
        const encodedTweetBody = createBodyAndEncode(props.tenantName, url);
        window.open(getTwitterLink(encodedTweetBody));
    }

    const onLinkedInClicked = async() => {
        const url = await onLinkRequested(linkDetails);
        const encodedPostBody = createBodyAndEncode(props.tenantName, url);
        window.open(getLinkedInLink(encodedPostBody))
    }

    const onFacebookClicked = async() => {
        const url = await onLinkRequested(linkDetails);
        const encodedPostBody = createBodyAndEncodeOnlyUrl(url);
        window.open(getFacebookLink(encodedPostBody))
    }

    const onEmailClicked = async() => {
        const subject = getDefaultEmailSubject(props.contentType, props.tenantName, props.contentTitle);
        const body = getDefaultEmailBody(props.contentType, props.tenantName);
        
        const url = await onLinkRequested(linkDetails);
        const mailToUrl = generateMailTo(subject, body, url);
        window.open(mailToUrl);
    }

    const onLinkClicked = async() => {
        const url = await onLinkRequested(linkDetails);
        navigator.clipboard.writeText(url);
        setSnackbarAndText(LINK_COPIED_SNACKBAR_TEXT, true);
    }

    return (
        <div className="share-screen">
            <div className="contents">

                <IconButton className="back-button" onClick={props.onCloseDialog}>
                    <ArrowBack className="icon-styling"/>
                </IconButton>

                <div className="body" style={{marginTop: "40%"}}>
                    <span className="share-label"> {headerLabel} </span>

                    <div className="share-options-container"> 
                        <LabelledIconButton 
                            label={"Facebook"}
                            icon={ 
                                <Icon className="social-media-icon">
                                    <img 
                                        src={"/images/socialMediaLogos/facebook.svg"} 
                                        alt="Facebook Logo" 
                                        height={"28px"} 
                                        width={"28px"}
                                    ></img>
                                </Icon> 
                            }
                            onClick={onFacebookClicked}
                        />

                        <LabelledIconButton 
                            label={"X"}
                            icon={ 
                                <Icon className="social-media-icon">
                                    <img 
                                        src={"/images/socialMediaLogos/x.svg"} 
                                        alt="X / Twitter Logo" 
                                        height={"22px"} 
                                        width={"22px"}
                                    ></img>
                                </Icon> 
                            }
                            onClick={onXClicked}
                        />

                        <LabelledIconButton 
                            label={"LinkedIn"}
                            icon={ 
                                <Icon className="social-media-icon">
                                    <img 
                                        src={"/images/socialMediaLogos/linkedInCropped.svg"} 
                                        alt="LinkedIn Logo" 
                                        height={"22px"} 
                                        width={"22px"}
                                        style={{position: "relative", left: "1px"}}
                                    ></img>
                                </Icon> 
                            }
                            onClick={onLinkedInClicked}
                        />

                        <LabelledIconButton 
                            label={"Email"}
                            icon={ <EmailOutlined className="icon-styling" /> }
                            onClick={onEmailClicked}
                        />

                        <LabelledIconButton 
                            label={"Copy link"}
                            icon={ <Link className="icon-styling"/> }
                            onClick={onLinkClicked}
                        />
                    </div>
                </div>
            </div>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={null}
                onClose={() => {
                    setSnackbarOpen(false);
                }}
                ContentProps={{
                    style: { minWidth: "50px", backgroundColor: "#36404A" }
                }}
                message={snackbarText}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            />
        </div>
    );
}

export default PublicShareScreen;